import { inject } from "@angular/core";
import { patchState, signalStoreFeature, type, withMethods } from "@ngrx/signals";
import { rxMethod } from "@ngrx/signals/rxjs-interop";
import { tapResponse } from "@ngrx/operators";
import { IAuthenticationState } from "./auth.state";
import { AuthLogin } from "../../models/auth/auth-login.model";
import { exhaustMap, pipe, tap } from "rxjs";
import { HttpErrorResponse } from "@angular/common/http";
import { AuthService } from "../../services/auth/auth.service";
import { AuthToken } from "../../models/auth/auth-token.model";
import { withAuthenticationStateMethods } from "./auth.methods.state";
import { Router } from "@angular/router";
import { clearCookie } from "../../utils/cookie.util";
import { GlobalSpinnerService } from "../../services/global-spinner/global-spinner.service";
import { NotificationService } from "../../services/notification/notification.service";
import { UserProfileService } from "../../services/user-profile/user-profile.service";
import { UserProfileStore } from "../user-profile/user-profile.store";
import { TenantContextService } from "../../services/tenant/tenant-context.service";

export const withAuthenticationApiMethods = signalStoreFeature(
    { state: type<IAuthenticationState>() },
    withAuthenticationStateMethods,
    withMethods((store) => {
        const authService = inject(AuthService);
        const notificationService = inject(NotificationService);
        const userProfileStore = inject(UserProfileStore);
        const userProfileService = inject(UserProfileService);
        const tenantContext = inject(TenantContextService);
        const router = inject(Router);

        const login = rxMethod<AuthLogin>(
            pipe(
                tap(() => {
                    patchState(store, { loading: true, error: null });
                }),
                exhaustMap((authLogin) =>
                    authService.login(authLogin).pipe(
                        tapResponse({
                            next: (response: { authToken: AuthToken }) => {
                                store.storeAuthToken(response.authToken);
                                if (response.authToken.profile) {
                                    userProfileStore.setUserProfile(response.authToken.profile);
                                }
                            },
                            error: (error: HttpErrorResponse) => {
                                const message = error.error?.message ?? error.message;
                                patchState(store, { loading: false, error: message });
                                notificationService.notifyError('Error', message);
                            },
                        })
                    )
                )
            )
        );

        const logout = () => {
            // Trigger server logout first so guards can see logout-in-progress immediately.
            authService.logout();
            store.clearAuth();
            userProfileStore.clearUserProfile();
            clearCookie('token');
            clearCookie('refreshToken');
            // Keep workspace identity and theme so the next login stays on the same company look.
            const tenantGuid = tenantContext.getTenantGuid();
            const companyCode = tenantContext.getCompanyCode();
            const savedTheme = localStorage.getItem('trieon-ui-theme');
            const savedColorMode = localStorage.getItem('trieon-ui-color-mode');
            localStorage.clear();
            if (tenantGuid) {
                tenantContext.setTenantGuid(tenantGuid);
            }
            if (companyCode) {
                tenantContext.setCompanyCode(companyCode);
            }
            if (savedTheme) {
                localStorage.setItem('trieon-ui-theme', savedTheme);
            }
            if (savedColorMode) {
                localStorage.setItem('trieon-ui-color-mode', savedColorMode);
            }
            router.navigate(['/auth/login']);
        };

        const refreshSession = (): Promise<void> => {
            return new Promise((resolve) => {
                // Avoid restoring session while a logout request is in progress.
                if (authService.isLogoutInProgress()) {
                    resolve();
                    return;
                }

                authService.isAuthenticated().subscribe({
                    next: (isAuthenticated: boolean) => {
                        if (isAuthenticated) {
                            patchState(store, { isAuthenticated: true, loading: false, error: null });
                            // Restore profile from localStorage; if missing, fetch fresh from API.
                            userProfileStore.rehydrateFromStorage();
                            if (!userProfileStore.userProfile()) {
                                userProfileService.getProfile().subscribe({
                                    next: (profile) => userProfileStore.setUserProfile(profile),
                                    error: () => { /* silent – redirect will happen via auth guard */ }
                                });
                            }
                            resolve();
                            return;
                        }

                        authService.refreshToken().subscribe({
                            next: (response: { authToken: AuthToken }) => {
                                store.storeAuthToken(response.authToken);
                                userProfileStore.setUserProfile(response.authToken.profile);
                                resolve();
                            },
                            error: () => resolve(),
                        });
                    },
                    error: () => resolve(),
                });
            });
        };

        return { login, logout, refreshSession };
    })
);